import React, { useEffect } from 'react';
import {
  type FieldValues,
  type SubmitErrorHandler,
  type UseFormSetError,
  useForm
} from 'react-hook-form';

/**
 * What a submit handler may still do to the form once validation has passed:
 * report a field error found only then (e.g. by a check that must also cover
 * fields that are not mounted).
 */
export type FormSubmitHelpers = { setError: UseFormSetError<FieldValues> };

/**
 * Props of the form.
 * @property {any} defaultValues - Initial values of the form.
 * @property {any} children - The children of the form component are the form fields.
 * @property onSubmit - Function that will be called when the form is submitted.
 * @property onInvalid - Called instead of onSubmit when a mounted field fails its rules.
 * @property {string} className - Class name that will be applied to the form element.
 * @property {string} id - Id that will be applied to the form element.
 */
type FormProps = {
  defaultValues?: any;
  children?: any;
  /** Receives (values, event) as before, plus the helpers. */
  onSubmit: (
    values: any,
    event: React.BaseSyntheticEvent | undefined,
    helpers: FormSubmitHelpers
  ) => unknown;
  onInvalid?: SubmitErrorHandler<FieldValues>;
  className?: string;
  id?: string;
};

/**
 * Returns a form with the given props, and includes 'react-hook-form' functionality.
 * @param {FormProps}  - FormProps
 * @returns A form component that takes in a defaultValues object, children, onSubmit function, and a
 * className string.
 */
export function Form({
  defaultValues,
  children,
  onSubmit,
  onInvalid,
  className,
  id
}: FormProps) {
  const {
    reset,
    handleSubmit,
    register,
    setError,
    formState: { errors }
  } = useForm({ defaultValues });

  useEffect(() => {
    // you can do async server request and fill up form
    reset({
      ...defaultValues
    });
  }, [defaultValues, reset]);

  function injectFormProps(node: any): any {
    if (!React.isValidElement(node)) {
      return node;
    }

    const childProps: Record<string, any> = {};

    if (node.props?.name) {
      childProps.register = register;
      childProps.errors = errors;
      childProps.key = node.props.name;
    }

    if (node.props?.children) {
      childProps.children = React.Children.map(
        node.props.children,
        injectFormProps
      );
    }

    return React.cloneElement(node, childProps);
  }

  return (
    <form
      onSubmit={handleSubmit(
        (values, event) => onSubmit(values, event, { setError }),
        onInvalid
      )}
      className={className}
      id={id}
    >
      {React.Children.map(children, injectFormProps)}
    </form>
  );
}
